import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { ErrorHandlingService } from '../services/error-handling.service';

/**
 * Interceptor HTTP para manejo centralizado de errores HTTP
 * 
 * Funcionalidades:
 * - Procesa errores HTTP usando ErrorHandlingService para logging consistente
 * - Mantiene compatibilidad: los servicios siguen recibiendo HttpErrorResponse
 * 
 * Nota: Este interceptor NO modifica el error que reciben los servicios,
 * solo procesa los errores para logging. El manejo de logout/redirección para errores
 * 401/403 debe hacerse en los servicios o componentes específicos.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const errorHandlingService = inject(ErrorHandlingService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Procesar el error usando ErrorHandlingService (para logging centralizado)
      const processedError = errorHandlingService.processError(error, {
        operation: extractOperationFromUrl(req.url)
      });

      // Si es un error de conexión (status 0, ej. ERR_INTERNET_DISCONNECTED o servidor apagado)
      if (error.status === 0) {
        errorHandlingService.showNetworkAlert();
      }

      // NOTA: No hacemos logout automático aquí porque un 401/403 puede venir del backend
      // por razones que no son de autenticación (como validación de datos).
      // El manejo de logout debe hacerse en los servicios o componentes que manejan
      // específicamente errores de autenticación.

      // Re-lanzar el error original (HttpErrorResponse) para que los servicios lo manejen
      // Esto mantiene la compatibilidad con el código existente
      return throwError(() => error);
    })
  );
};

/**
 * Extrae el nombre de la operación desde la URL para contexto en el logging
 */
function extractOperationFromUrl(url: string): string {
  try {
    const urlParts = url.split('/').filter(part => part.length > 0);
    
    // Intentar extraer el recurso/acción de la URL
    // Ejemplos:
    // - /v1/Auth/Login -> "Auth/Login"
    // - /v1/Quotes/123 -> "Quotes/get"
    // - /v1/Catalog/VehicleMakes -> "Catalog/VehicleMakes"
    
    if (urlParts.length >= 2) {
      // Si la URL tiene formato /v1/Resource/...
      const resourceIndex = urlParts.findIndex(part => part.match(/^v\d+$/)) + 1;
      if (resourceIndex > 0 && urlParts[resourceIndex]) {
        const resource = urlParts[resourceIndex];
        const action = urlParts[resourceIndex + 1] || 'get';
        return `${resource}/${action}`;
      }
    }
    
    // Fallback: usar el último segmento de la URL
    return urlParts[urlParts.length - 1] || 'unknown';
  } catch {
    return 'unknown';
  }
}
